import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, Globe2, X } from "lucide-react";
import { LANGUAGE_STORAGE_KEY } from "../i18n";

const LANGUAGES = [
  { code: "en", short: "EN", label: "English", flag: "🇬🇧" },
  { code: "ru", short: "RU", label: "Русский", flag: "🇷🇺" },
  { code: "uz", short: "UZ", label: "O‘zbekcha", flag: "🇺🇿" },
];

const LanguageSwitcher = () => {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const switcherRef = useRef(null);
  const currentLanguage = LANGUAGES.find((language) =>
    i18n.language?.startsWith(language.code)
  ) || LANGUAGES[0];

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (!switcherRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  const changeLanguage = (lng) => {
    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, lng);
    } catch {
      // The language still changes for this session if storage is unavailable.
    }
    i18n.changeLanguage(lng);
    setOpen(false);
  };

  return (
    <div ref={switcherRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("language_switcher.choose")}
        className="h-10 inline-flex items-center gap-2 rounded-full border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-800/70 px-3 text-sm font-bold text-slate-800 dark:text-slate-100 hover:border-amber-400 dark:hover:border-amber-400 transition-colors"
      >
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-400/15">
          <Globe2 className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
        </span>
        {currentLanguage.short}
        <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+0.75rem)] z-[70] w-64 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-[#0d1016] p-2 shadow-2xl shadow-slate-950/20"
        >
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-100 dark:border-slate-800 mb-2">
            <p className="font-bold text-slate-900 dark:text-white">
              {currentLanguage.code === "ru"
                ? "Язык"
                : currentLanguage.code === "uz"
                ? "Til"
                : "Language"}
            </p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t("language_switcher.close")}
              className="h-8 w-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {LANGUAGES.map((language) => {
            const isSelected = currentLanguage.code === language.code;
            return (
              <button
                key={language.code}
                type="button"
                role="menuitemradio"
                aria-checked={isSelected}
                onClick={() => changeLanguage(language.code)}
                className={`w-full flex items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors ${
                  isSelected
                    ? "bg-amber-400 text-slate-950"
                    : "text-slate-700 dark:text-slate-200 hover:bg-amber-50 dark:hover:bg-slate-800"
                }`}
              >
                <span className="text-2xl leading-none" aria-hidden>{language.flag}</span>
                <span className="flex-1">
                  <span className="block text-sm font-bold">{language.label}</span>
                  {isSelected && (
                    <span className="block text-[10px] font-bold uppercase tracking-widest opacity-70">
                      {currentLanguage.code === "ru"
                        ? "Выбран"
                        : currentLanguage.code === "uz"
                        ? "Tanlangan"
                        : "Selected"}
                    </span>
                  )}
                </span>
                {isSelected && <Check className="h-4 w-4" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
